import * as d3 from "d3";
import {FlowDetail, FlowRessourceObject} from "@app/netscope/model/data-models";
import {NetscopeCommonFlowviewComponent} from "@app/netscope/netscope-common-flowview/netscope-common-flowview.component";
import {ClustersViewerRenderer} from "@app/netscope/netscope-common-flowview/renderers/ClustersViewerRenderer";
import colors from "@app/netscope/netscope-common-flowview/renderers/ClustersViewerRenderer";

export class DependenciesViewerRenderer extends ClustersViewerRenderer {

    override async render(flowComponent: NetscopeCommonFlowviewComponent): Promise<void> {
        this.flowComponent = flowComponent;

        let graphData = await this.flowComponent.flowsRepository.generateGraphData();

        // Ensure we don't reuse previous coordinates from past rendering (before a switch focus)
        if (flowComponent.graphParameters.lastTransformation != undefined) {
          flowComponent.graphParameters.userMovedMap = false;
          flowComponent.graphParameters.lastTransformation.x = flowComponent.graphParameters.firstTransformation.x;
          flowComponent.graphParameters.lastTransformation.y = flowComponent.graphParameters.firstTransformation.y;
          flowComponent.graphParameters.lastTransformation.k = flowComponent.graphParameters.firstTransformation.k;
        }

        this.flowComponent.datatableTabFlowsRepository = this.flowComponent.flowsRepository.clone();
        this.flowComponent.datatableTabFlowsRepository.setWhereClause(" src_resource_type='vm' and dst_resource_type='vm' ")
        this.flowComponent.datatableTabFlowsRepository.showFirstPage();

        // let visualisationData = this.computeVisualisationDataForRenderers(graphData, flowComponent);

        let links = graphData.links.filter((link) => link.source.host !== undefined && link.target.host !== undefined);
        let vms = graphData.nodes
          .filter((vm) => vm.host !== undefined)
          .sort((vmA, vmB) => vmA.host.uuid.localeCompare(vmB.host.uuid));
        let focusedVmsUuids = [];

        let hostsSet = new Set<FlowRessourceObject>();
        for (let vm of vms) {
          if (vm.host !== undefined) {
            hostsSet.add(vm.host);
          }
        }
        let hosts = Array.from(hostsSet.values());

        //
        // START RENDERING GRAPHICS
        //

        let svgHeight = document.getElementById('divFlowsSvg').clientHeight;
        let svgWidth = document.getElementById('divFlowsSvg').clientWidth;

        if (svgHeight == 0) {
            svgHeight = flowComponent.lastSvgHeight;
        } else {
            flowComponent.lastSvgHeight = svgHeight;
        }

        if (svgWidth == 0) {
            svgWidth = flowComponent.lastSvgWidth;
        } else {
            flowComponent.lastSvgWidth = svgWidth;
        }

        const viewCenterX = 0
        const viewCenterY = 0;

        // The names of the VMs are written horizontally under them. The circle is made large enough for the middle
        // of each name to stay clear of the other VMs and of the middle of the other names: where the circle is
        // vertical, the VMs are then far enough apart for a name to fit between a VM and the one under it. The ends
        // of the longest names are left out, keeping them clear would take a much larger circle, while they only
        // brush a neighbour where the circle is horizontal.
        const labelFontSize = 13;
        // Distance from the center of a VM to the top of its name, the circle of the VM being drawn with a 5px stroke
        const vmOuterRadius = flowComponent.nodeRadius + 3;
        const labelTop = vmOuterRadius + 2;
        const labelHeight = labelFontSize + 4;
        const checkedLabelWidth = 60;

        let measureContext = document.createElement("canvas").getContext("2d");
        measureContext.font = `${labelFontSize}px ${getComputedStyle(document.body).fontFamily}`;
        const labelWidths = vms.map((vm) => measureContext.measureText(vm.name ?? "").width + 4);
        const maxLabelWidth = Math.max(0, ...labelWidths);

        // Boxes taken by each VM and the middle of its name once the VMs are spread on a circle of the given radius
        type Box = { x0: number, y0: number, x1: number, y1: number };
        let boxesAt = (radius: number): { vm: Box, label: Box }[] => {
            return vms.map((vm, index) => {
                let angle = 2 * Math.PI * index / vms.length;
                let x = viewCenterX + Math.cos(angle) * radius;
                let y = viewCenterY + Math.sin(angle) * radius;
                let halfLabelWidth = Math.min(labelWidths[index], checkedLabelWidth) / 2;
                return {
                    vm: {x0: x - vmOuterRadius, y0: y - vmOuterRadius, x1: x + vmOuterRadius, y1: y + vmOuterRadius},
                    label: {x0: x - halfLabelWidth, y0: y + labelTop, x1: x + halfLabelWidth, y1: y + labelTop + labelHeight}
                };
            });
        };

        let overlap = (a: Box, b: Box): boolean => a.x0 < b.x1 && b.x0 < a.x1 && a.y0 < b.y1 && b.y0 < a.y1;

        let hasCollisions = (radius: number): boolean => {
            let boxes = boxesAt(radius);
            for (let i = 0; i < boxes.length; i++) {
                for (let j = 0; j < boxes.length; j++) {
                    if (i == j) {
                        continue;
                    }
                    if (overlap(boxes[i].vm, boxes[j].vm) || overlap(boxes[i].label, boxes[j].vm)
                        || (j > i && overlap(boxes[i].label, boxes[j].label))) {
                        return true;
                    }
                }
            }
            return false;
        };

        // Smallest radius free of collisions, grown step by step from the one leaving room for the VMs themselves
        let requiredRadius = Math.max(100.0, vms.length * (2 * vmOuterRadius + 6) / (2 * Math.PI));
        while (requiredRadius < 50000 && hasCollisions(requiredRadius)) {
            requiredRadius *= 1.02;
        }

        // Distance from the circle to the arcs of the hosts
        const hostArcOffset = 30;
        const hostArcThickness = 20;
        // Distance from the circle to the outer edge of the graph: the names of the hosts, or the names of the VMs
        // on the sides of the circle, which stick out of it by half their width
        const outerOffset = Math.max(hostArcOffset + hostArcThickness + 30, maxLabelWidth / 2 + 10);

        // The circle is as large as the screen allows, and larger when its VMs and their names would not fit
        // around it: the view is then zoomed out so that the whole graph, names and hosts included, shows up at
        // first
        const availableRadius = Math.min(svgWidth, svgHeight) / 2 * 0.95;
        let zoomFor = (radius: number): number => Math.min(1.0, availableRadius / (radius + outerOffset));

        let vmsOrbitCircleRadius = Math.max(requiredRadius, availableRadius - outerOffset);
        let zoomRatio = zoomFor(vmsOrbitCircleRadius);

        let ratioMinSizeOverMaxSize = 50;

        d3.select('div#divFlowsSvg').select("svg").remove();
        const svg = d3.select('div#divFlowsSvg')
            .append('svg')
            .attr('width', svgWidth)
            .attr('height', svgHeight)
            .on("click", (d, i) => {
                // Remove a previously open dropdown menus
                let existingDropdowns = document.getElementsByClassName("dropdown_actions");

                for (let dropdown of Array.from(existingDropdowns)) {
                    dropdown.remove();
                }
            });
        flowComponent.svg = svg;

        // Add zoom
        svg.call(d3.zoom()
            .extent([
                [0, 0],
                [svgWidth, svgHeight]
            ])
            .scaleExtent([0.1, ratioMinSizeOverMaxSize])
            .on("zoom", zoomed));

        const g = svg.append("g");

        const self = flowComponent;

        // Add hosts UUID to vms
        let vmUuidToHostIndex = {};

        graphData.nodes
          .filter((n) => n.type === "host")
          .filter((router) => router.vms != undefined)
          .map((router) => router.vms.map((vm) => {
            delete router.minAngle;
            delete router.maxAngle;
            vmUuidToHostIndex[vm.uuid] = router;
          }));

        graphData.nodes.filter((n) => n.type === "vm").map((vm) => {
          if (vm.host !== undefined) {
            delete vm.host.minAngle;
            delete vm.host.maxAngle;
            vmUuidToHostIndex[vm.uuid] = vm.host;
          }
        });

        const angleIncrementVms = 2 * Math.PI / vms.length;
        let currentAngleVm = 0;
        vms.forEach((vm) => {
            vm.angle = currentAngleVm;
            vm.fx = viewCenterX + Math.cos(vm.angle) * vmsOrbitCircleRadius;
            vm.fy = viewCenterY + Math.sin(vm.angle) * vmsOrbitCircleRadius;
            vm.linefx = viewCenterX + Math.cos(vm.angle) * (vmsOrbitCircleRadius - flowComponent.nodeRadius - 10);
            vm.linefy = viewCenterY + Math.sin(vm.angle) * (vmsOrbitCircleRadius - flowComponent.nodeRadius - 10);

            // Define angle in host
            let host = vmUuidToHostIndex[vm.uuid];

            if (host !== undefined) {
              if (!host.hasOwnProperty("minAngle")) {
                  host.minAngle = vm.angle;
              }
              host.maxAngle = vm.angle;
            }

            currentAngleVm += angleIncrementVms;
        });

        // D3's arcs is shifted compared to a trigonometric circle: while "0 deg rad" on a trigonometric circle is the
        // middle left point of the circle, on D3's arc "0 deg rad" it is the top middle point of the circle: to
        // make D3'es arc coordinates matches those of classic trigonometry we have to shift angles by PI / 2 on the right
        let shift = (1) * Math.PI / 2;

        // Adjust angles of hosts
        hosts.map((host) => {
            let coordinates = [host.minAngle + shift, host.maxAngle + shift];
            host.minAngle = Math.min(...coordinates) - angleIncrementVms / 4;
            host.maxAngle = Math.max(...coordinates) + angleIncrementVms / 4;
        });

        function zoomed({transform}) {
            self.graphParameters.userMovedMap = true;
            self.graphParameters.lastTransformation = transform;
            g.attr("transform", transform);
        }

        // Draw circle for vms
        g.append('g').append('circle')
            .attr('cx', viewCenterX)
            .attr('cy', viewCenterY)
            .attr('r', vmsOrbitCircleRadius)
            .attr('fill', 'transparent')
            .attr('stroke', 'rgba(200, 200, 200, 0.3)')
            //.attr('stroke', 'var(--highcharts-neutral-color-100)')
            //.attr('stroke', '#fff')
            .attr('stroke-width', '2px');

        const defs = g.append('svg:defs');

        const data = [{
            id: 0,
            name: 'circle',
            color: '#ddd',
            path: 'M 0, 0  m -5, 0  a 5,5 0 1,0 10,0  a 5,5 0 1,0 -10,0',
            viewbox: '-6 -6 12 12'
        }, {
            id: 1,
            name: 'square',
            color: '#ddd',
            path: 'M 0,0 m -5,-5 L 5,-5 L 5,5 L -5,5 Z',
            viewbox: '-5 -5 10 10'
        }, {
            id: 2,
            name: 'arrow',
            color: '#ddd',
            path: 'M 0,0 m -5,-5 L 2,0 L -5,5 Z',
            viewbox: '-10 -5 13 10'
        }, {
            id: 2,
            name: 'arrow_reversed',
            color: '#ddd',
            path: 'M 0,0 m -5,-5 L 2,0 L -5,5 Z',
            viewbox: '-10 -5 13 10',
            reversed: true
        }, {
            id: 3,
            name: 'stub',
            color: '#ddd',
            path: 'M 0,0 m -1,-5 L 1,-5 L 1,5 L -1,5 Z',
            viewbox: '-1 -5 2 10'
        }, {
            id: 4,
            name: 'focus_circle',
            color: colors.focused,
            path: 'M 0, 0  m -5, 0  a 5,5 0 1,0 10,0  a 5,5 0 1,0 -10,0',
            viewbox: '-6 -6 12 12'
        }, {
            id: 5,
            name: 'focus_square',
            color: colors.focused,
            path: 'M 0,0 m -5,-5 L 5,-5 L 5,5 L -5,5 Z',
            viewbox: '-5 -5 10 10'
        }, {
            id: 6,
            name: 'focus_arrow',
            color: colors.focused,
            path: 'M 0,0 m -5,-5 L 2,0 L -5,5 Z',
            viewbox: '-10 -5 13 10'
        }, {
            id: 6,
            name: 'focus_arrow_reversed',
            color: colors.focused,
            path: 'M 0,0 m -5,-5 L 2,0 L -5,5 Z',
            viewbox: '-10 -5 13 10',
            reversed: true
        }, {
            id: 7,
            name: 'focus_stub',
            color: colors.focused,
            path: 'M 0,0 m -1,-5 L 1,-5 L 1,5 L -1,5 Z',
            viewbox: '-1 -5 2 10'
        }];

        const margin = {
            top: 100,
            right: 100,
            bottom: 100,
            left: 100
        };

        const paths = g.append('svg:g')
            .attr('id', 'markers')
            .attr('transform', 'translate(' + margin.left + ',' + margin.top + ')');

        const marker = defs.selectAll('marker')
            .data(data)
            .enter()
            .append('svg:marker')
            .attr('id', function(d) {
                return 'marker_' + d.name;
            })
            // Sized in the units of the graph, not of the stroke: the links are as wide as their traffic, and the
            // arrows of the largest ones would otherwise cover their VMs
            .attr('markerHeight', 9)
            .attr('markerWidth', 9)
            .attr('markerUnits', 'userSpaceOnUse')
            .attr('orient', function(d) {
                if (d.reversed === true) {
                    return 'auto-start-reverse';
                }
                return 'auto';
            })
            .attr('refX', 0)
            .attr('refY', 0)
            .attr('viewBox', function(d) {
                return d.viewbox;
            })
            .append('svg:path')
            .attr('d', function(d) {
                return d.path;
            })
            .attr('fill', function(d, i) {
                return d.color;
            });

        // Place hosts and vms

        // First, place hosts
        let visibleHosts = hosts.filter((host) => host?.visible !== false);
        const hostsNodes = g.append('g')
            .attr('class', 'hostsArc')
            .selectAll('g')
            .data(visibleHosts)
            .enter().append('g');

        const hostsArcs = hostsNodes
            .append("path")
            .attr("d", (d) => {
                let arc = d3.arc()
                    .innerRadius(vmsOrbitCircleRadius + hostArcOffset)
                    .outerRadius(vmsOrbitCircleRadius + hostArcOffset + hostArcThickness)
                    .startAngle(d.minAngle)
                    .endAngle(d.maxAngle)
                // @ts-ignore
                return arc();
            })
            .attr("fill", "rgb(145,64,184)");

        //Create an SVG path (based on bl.ocks.org/mbostock/2565344)
        hostsNodes
            .append("path")
            .attr("id", (d) => `arc_text_${d.uuid}`) //Unique id of the path
            .attr("d", (d) => {
                let startAngle = d.minAngle;
                let endAngle = d.maxAngle;

                let flip = false;

                let radius = vmsOrbitCircleRadius + hostArcOffset + hostArcThickness + (flip ? 5 : 15);

                let arc = d3.arc()
                    .innerRadius(radius)
                    .outerRadius(radius + 1)
                    .startAngle(startAngle)
                    .endAngle(endAngle)
                // @ts-ignore
                return arc();
            })
            .style("fill", "none");

        //Create an SVG text element and append a textPath element
        hostsNodes.append("text")
            .append("textPath") //append a textPath to the text element
            .attr("xlink:href", (d) => `#arc_text_${d.uuid}`) //place the ID of the path here
            .style("text-anchor", "middle") //place the text halfway on the arc
            .attr("startOffset", (d) => {
                let startAngle = d.minAngle;
                let endAngle = d.maxAngle;

                let flip = (startAngle + endAngle) / 2 > Math.PI ? true : false;

                return flip ? "25%" : "75%";
            })
            .attr("stroke", "rgb(145,64,184)")
            .text((d) => d.name);

        // Links are drawn with hierarchical edge bundling (https://gist.github.com/mbostock/1044242): the VMs are
        // the leaves of a root -> hosts -> VMs tree, and a link follows the path of that tree between its two VMs,
        // through the anchors of their hosts and the root. The curve is pulled towards that path, so that the links
        // between the same hosts are drawn as one bundle.
        const bundlingStrength = 0.85;
        const hostAnchorsRadius = 0.55 * vmsOrbitCircleRadius;

        let vmsByUuid = new Map(vms.map((vm) => [vm.uuid, vm]));

        // The VMs of a host are contiguous on the circle: its anchor lies on the bisector of their arc. The arc of
        // a host is shifted for d3.arc (see above), which is taken back here.
        let hostAnchor = (host) => {
            let middleAngle = (host.minAngle + host.maxAngle) / 2 - shift;
            return {
                x: viewCenterX + Math.cos(middleAngle) * hostAnchorsRadius,
                y: viewCenterY + Math.sin(middleAngle) * hostAnchorsRadius
            };
        };

        // The flows of a pair of VMs are drawn as a single link, whatever their direction: its width follows the
        // bytes they exchanged, and clicking it lists the flows of both directions.
        type BundledLink = { flow: FlowDetail, source: any, target: any, exchangedBytes: number };
        let bundledLinksByPair: Map<string, BundledLink> = new Map();

        for (let link of links) {
            let sourceVm = vmsByUuid.get(link.source.uuid);
            let targetVm = vmsByUuid.get(link.target.uuid);

            if (sourceVm === undefined || targetVm === undefined || sourceVm === targetVm) {
                continue;
            }

            let pairKey = [sourceVm.uuid, targetVm.uuid].sort().join("|");
            if (!bundledLinksByPair.has(pairKey)) {
                bundledLinksByPair.set(pairKey, {flow: link, source: sourceVm, target: targetVm, exchangedBytes: 0});
            }
            bundledLinksByPair.get(pairKey).exchangedBytes += link.exchanged_bytes ?? 0;
        }

        // The largest links are drawn first, so that they do not hide the thin ones drawn over them
        let bundledLinks = Array.from(bundledLinksByPair.values())
            .sort((linkA, linkB) => linkB.exchangedBytes - linkA.exchangedBytes);

        // A square root scale, so that a link twice as wide carries four times the traffic, and the small flows
        // stay visible next to the large ones
        const maxExchangedBytes = d3.max(bundledLinks, (link) => link.exchangedBytes) || 1;
        const linkWidth = d3.scaleSqrt().domain([0, maxExchangedBytes]).range([1, 14]);
        const linkOpacity = d3.scaleSqrt().domain([0, maxExchangedBytes]).range([0.35, 0.8]);
        const linkColor = "rgb(140, 140, 140)";

        // Arrow heads grow with their link, or a wide link would look like a line holding a small arrow. A marker
        // cannot be sized per link, so one is defined per rounded width: there are at most as many as pixels in
        // the widest link. The arrow is drawn after the end of the link, its base on it, so that the two do not
        // overlap and darken each other.
        const arrowPath = 'M -5,-5 L 2,0 L -5,5 Z';
        const arrowViewBox = {minX: -5, width: 7, height: 10};

        let roundedWidth = (link: BundledLink): number => Math.round(linkWidth(link.exchangedBytes));
        let arrowHeight = (width: number): number => Math.max(9, 2.4 * width);
        let arrowLength = (width: number): number => arrowHeight(width) * arrowViewBox.width / arrowViewBox.height;

        let arrowMarkerId = (width: number, focused: boolean, reversed: boolean): string => {
            return `dependencies_${focused ? 'focus_' : ''}arrow${reversed ? '_reversed' : ''}_${width}`;
        };

        for (let width of new Set(bundledLinks.map(roundedWidth))) {
            for (let focused of [false, true]) {
                for (let reversed of [false, true]) {
                    defs.append('svg:marker')
                        .attr('id', arrowMarkerId(width, focused, reversed))
                        .attr('markerUnits', 'userSpaceOnUse')
                        .attr('markerWidth', arrowLength(width))
                        .attr('markerHeight', arrowHeight(width))
                        .attr('viewBox', `${arrowViewBox.minX} -5 ${arrowViewBox.width} ${arrowViewBox.height}`)
                        .attr('preserveAspectRatio', 'none')
                        // The base of the arrow sits on the end of the link
                        .attr('refX', arrowViewBox.minX)
                        .attr('refY', 0)
                        .attr('orient', reversed ? 'auto-start-reverse' : 'auto')
                        .append('svg:path')
                        .attr('d', arrowPath)
                        .attr('fill', focused ? colors.focused : linkColor)
                        // As transparent as the links of that width, the width following the traffic as the
                        // opacity does
                        .attr('fill-opacity', focused ? 0.9 : linkOpacity(linkWidth.invert(width)));
                }
            }
        }

        let arrowMarker = (link: BundledLink, focused: boolean, reversed: boolean): string => {
            return `url(#${arrowMarkerId(roundedWidth(link), focused, reversed)})`;
        };

        type Point = {x: number, y: number};

        const basisLine = d3.line<Point>()
            .x((point) => point.x)
            .y((point) => point.y)
            .curve(d3.curveBasis);

        // What d3.curveBundle does: the points between the two ends are pulled towards the straight line joining
        // them, less as the bundling is stronger. It is done here so that the points bringing the ends of a link
        // along the radius of its VMs are kept out of it: they have to stay on that radius.
        let straightened = (points: Point[]): Point[] => {
            let first = points[0];
            let last = points[points.length - 1];
            let count = points.length - 1;

            return points.map((point, index) => {
                if (index == 0 || index == count) {
                    return point;
                }
                let t = index / count;
                return {
                    x: bundlingStrength * point.x + (1 - bundlingStrength) * (first.x + (last.x - first.x) * t),
                    y: bundlingStrength * point.y + (1 - bundlingStrength) * (first.y + (last.y - first.y) * t)
                };
            });
        };

        // Point of the radius of a VM, at the given distance inside the circle
        let alongRadius = (vm, distanceInside: number) => {
            let radius = vmsOrbitCircleRadius - distanceInside;
            return {
                x: viewCenterX + Math.cos(vm.angle) * radius,
                y: viewCenterY + Math.sin(vm.angle) * radius
            };
        };

        // A link ends before its VM by the length of its arrow, so that the tip of the arrow touches the circle
        // of the VM. Its last stretch runs along the radius of the VM, so that its arrow points at the VM: the
        // arrow follows the direction the curve ends with, which the bundling would otherwise tilt.
        const vmEdgeDistance = flowComponent.nodeRadius + 3;
        const straightEndLength = 14;

        let linkEndPoints = (vm, link: BundledLink) => {
            let end = vmEdgeDistance + arrowLength(roundedWidth(link));
            return [alongRadius(vm, end), alongRadius(vm, end + straightEndLength)];
        };

        let linkPath = (link: BundledLink): string => {
            let sourceHost = vmUuidToHostIndex[link.source.uuid];
            let targetHost = vmUuidToHostIndex[link.target.uuid];

            let [sourceEnd, sourceStraightEnd] = linkEndPoints(link.source, link);
            let [targetEnd, targetStraightEnd] = linkEndPoints(link.target, link);

            // Path of the tree between the two VMs, the root being only on the path of VMs of different hosts
            let treePath: Point[] = [sourceEnd, hostAnchor(sourceHost)];
            if (sourceHost !== targetHost) {
                treePath.push({x: viewCenterX, y: viewCenterY});
                treePath.push(hostAnchor(targetHost));
            }
            treePath.push(targetEnd);

            let bundled = straightened(treePath);

            // A basis curve leaves its first point towards the second one, and reaches its last point from the one
            // before: the points along the radius make both ends of the link point at their VM
            return basisLine([sourceEnd, sourceStraightEnd, ...bundled.slice(1, -1), targetStraightEnd, targetEnd]);
        };

        let isFocusedLink = (link: BundledLink): boolean => {
            return focusedVmsUuids.indexOf(link.source.uuid) != -1 || focusedVmsUuids.indexOf(link.target.uuid) != -1;
        };

        let formatBytes = d3.format(".3~s");

        const linksPaths = g.append('g')
            .attr('class', 'links')
            .selectAll('path')
            .data(bundledLinks)
            .enter().append('path')
            .attr('d', linkPath)
            .attr('marker-start', (link) => arrowMarker(link, isFocusedLink(link), true))
            .attr('marker-end', (link) => arrowMarker(link, isFocusedLink(link), false))
            .style('fill', 'none')
            .style('stroke', (link) => isFocusedLink(link) ? colors.focused : linkColor)
            .style('stroke-opacity', (link) => linkOpacity(link.exchangedBytes))
            .style('stroke-width', (link) => `${roundedWidth(link)}`)
            // A round cap would reach into the base of the arrow
            .style('stroke-linecap', 'butt')
            .attr('pointer-events', 'visibleStroke')
            .on('mouseover', function(d, link: BundledLink) {
                d3.select(this).style('cursor', 'pointer');
                highlight([link]);
            })
            .on('mouseout', function(d, link: BundledLink) {
                d3.select(this).style('cursor', 'default');
                resetHighlight();
            })
            .on("click", function(d, link: BundledLink) {
                d.stopPropagation();
                let title = `Flows between ${link.source.name} and ${link.target.name}`;

                let subClauses = ["false"];
                subClauses.push(` (src_resource_uuid = '${link.source.uuid}' and dst_resource_uuid = '${link.target.uuid}' ) `);
                subClauses.push(` (dst_resource_uuid = '${link.source.uuid}' and src_resource_uuid = '${link.target.uuid}' ) `);

                let combinedSubClauses = subClauses.join(" or ");
                let whereClause = `where ${combinedSubClauses}`;

                flowComponent.askShowSidePanel(title, whereClause);
                flowComponent.askRefreshAngular();
            });

        linksPaths.append('title')
            .text((link) => `${link.source.name} ↔ ${link.target.name}: ${formatBytes(link.exchangedBytes)}B exchanged`);

        // Hovering a link, or a VM, brings forward the links concerned and the VMs at their ends, and fades
        // everything else, so that they can be told apart from the bundles they are part of
        let highlight = (highlightedLinks: BundledLink[], hoveredVmUuid?: string) => {
            let highlightedVmsUuids = new Set<string>(highlightedLinks.flatMap((link) => [link.source.uuid, link.target.uuid]));
            if (hoveredVmUuid !== undefined) {
                highlightedVmsUuids.add(hoveredVmUuid);
            }

            let isHighlighted = (link: BundledLink) => highlightedLinks.includes(link) || isFocusedLink(link);

            visuNode.style('opacity', (vm) => highlightedVmsUuids.has(vm.uuid) ? '100%' : '25%');

            linksPaths
                .style('stroke', (link) => isHighlighted(link) ? colors.focused : linkColor)
                .attr('marker-start', (link) => arrowMarker(link, isHighlighted(link), true))
                .attr('marker-end', (link) => arrowMarker(link, isHighlighted(link), false))
                .style('stroke-opacity', (link) => isHighlighted(link) ? 0.9 : linkOpacity(link.exchangedBytes))
                // The opacity of the whole path fades its arrows as well, which its stroke opacity would not
                .style('opacity', (link) => isHighlighted(link) ? 1 : 0.12)
                .filter(isHighlighted)
                .raise();
        };

        let resetHighlight = () => {
            visuNode.style('opacity', '100%');

            linksPaths
                .style('stroke', (link) => isFocusedLink(link) ? colors.focused : linkColor)
                .attr('marker-start', (link) => arrowMarker(link, isFocusedLink(link), true))
                .attr('marker-end', (link) => arrowMarker(link, isFocusedLink(link), false))
                .style('stroke-opacity', (link) => linkOpacity(link.exchangedBytes))
                .style('opacity', null)
                // Back to the drawing order, the largest links under the thin ones
                .order();
        };

        console.log(`graphData.links.length = ${graphData.links.length}, bundled into ${bundledLinks.length} links`);

        // Second, place VMs
        const nodes = vms;
        const visuNode = g.append('g')
            .attr('class', 'nodes')
            .selectAll('g')
            .data(nodes)
            .enter().append('g')
            .attr('transform', (d) => 'translate(' + d.fx + ',' + d.fy + ')');

        visuNode.on("click", function(d, i) {
            d.stopPropagation();
            // that.displayDropDownAction(d, i, this, g, graphData, flowComponent, focusedVmsUuids, visuNode);
            let title = `Flows of ${i.name}`;

            let subClauses = ["false"];
            subClauses.push(` (src_resource_uuid = '${i.uuid}' or dst_resource_uuid = '${i.uuid}' ) `);

            let combinedSubClauses = subClauses.join(" or ");
            let whereClause = `where ${combinedSubClauses}`;

            flowComponent.askShowSidePanel(title, whereClause, i.uuid, i);
            flowComponent.askRefreshAngular();
        });

        const vmCircles = visuNode
            .append('g')
            .append('circle')
            .attr('r', flowComponent.nodeRadius)
            .style('stroke-width', 5) // set the stroke width
            .style('stroke', (d) => {
                if (focusedVmsUuids.indexOf(d.uuid) !== -1 || focusedVmsUuids.indexOf(d.uuid) !== -1) {
                    return colors.focused;
                }
                if (d.type === "external_ip") {
                    return colors.external_ip;
                }
                if (d.type === "unknown_ip") {
                    return colors.unknown_ip;
                }
                return colors.infrastructure;
            })
            .attr('fill', (d) => 'white');

        // if (flowComponent.selectedAppsGroups.length > 0) {
        //     flowComponent.displayAppsGroupsLegend(visuNode);
        // }

        const iconsSvgCode = {
            "datacenter": "<path d=\"M26.5,4.08C22.77,4.08,19,5.4,19,7.91V9.5a18.75,18.75,0,0,1,2,.2V7.91c0-.65,2.09-1.84,5.5-1.84S32,7.27,32,7.91V18.24c0,.54-1.46,1.44-3.9,1.73v2c3.13-.32,5.9-1.6,5.9-3.75V7.91C34,5.4,30.23,4.08,26.5,4.08Z\"/><path d=\"M4,18.24V7.91c0-.65,2.09-1.84,5.5-1.84S15,7.27,15,7.91V9.7a18.75,18.75,0,0,1,2-.2V7.91c0-2.52-3.77-3.84-7.5-3.84S2,5.4,2,7.91V18.24C2,20.4,4.77,21.67,7.9,22V20C5.46,19.68,4,18.78,4,18.24Z\"/><path d=\"M18,10.85c-4.93,0-8.65,1.88-8.65,4.38V27.54c0,2.5,3.72,4.38,8.65,4.38s8.65-1.88,8.65-4.38V15.23C26.65,12.73,22.93,10.85,18,10.85Zm6.65,7.67c-.85,1-3.42,2-6.65,2A14.49,14.49,0,0,1,14,20v1.46a16.33,16.33,0,0,0,4,.47,12.76,12.76,0,0,0,6.65-1.56v3.12c-.85,1-3.42,2-6.65,2a14.49,14.49,0,0,1-4-.53v1.46a16.33,16.33,0,0,0,4,.47,12.76,12.76,0,0,0,6.65-1.56v2.29c0,.95-2.65,2.38-6.65,2.38s-6.65-1.43-6.65-2.38V15.23c0-.95,2.65-2.38,6.65-2.38s6.65,1.43,6.65,2.38Z\"/>",
            "network": "<path d=\"M26.58,32h-18a1,1,0,1,0,0,2h18a1,1,0,0,0,0-2Z\"/><path d=\"M17.75,2a14,14,0,0,0-14,14c0,.45,0,.89.07,1.33l0,0h0A14,14,0,1,0,17.75,2Zm0,2a12,12,0,0,1,8.44,3.48c0,.33,0,.66,0,1A18.51,18.51,0,0,0,14,8.53a2.33,2.33,0,0,0-1.14-.61l-.25,0c-.12-.42-.23-.84-.32-1.27s-.14-.81-.19-1.22A11.92,11.92,0,0,1,17.75,4Zm-3,5.87A17,17,0,0,1,25.92,10a16.9,16.9,0,0,1-3.11,7,2.28,2.28,0,0,0-2.58.57c-.35-.2-.7-.4-1-.63a16,16,0,0,1-4.93-5.23,2.25,2.25,0,0,0,.47-1.77Zm-4-3.6c0,.21.06.43.1.64.09.44.21.87.33,1.3a2.28,2.28,0,0,0-1.1,2.25A18.32,18.32,0,0,0,5.9,14.22,12,12,0,0,1,10.76,6.27Zm0,15.71A2.34,2.34,0,0,0,9.2,23.74l-.64,0A11.94,11.94,0,0,1,5.8,16.92l.11-.19a16.9,16.9,0,0,1,4.81-4.89,2.31,2.31,0,0,0,2.28.63,17.53,17.53,0,0,0,5.35,5.65c.41.27.83.52,1.25.76A2.32,2.32,0,0,0,19.78,20a16.94,16.94,0,0,1-6.2,3.11A2.34,2.34,0,0,0,10.76,22Zm7,6a11.92,11.92,0,0,1-5.81-1.51l.28-.06a2.34,2.34,0,0,0,1.57-1.79,18.43,18.43,0,0,0,7-3.5,2.29,2.29,0,0,0,3-.62,17.41,17.41,0,0,0,4.32.56l.53,0A12,12,0,0,1,17.75,28Zm6.51-8.9a2.33,2.33,0,0,0-.33-1.19,18.4,18.4,0,0,0,3.39-7.37q.75.35,1.48.78a12,12,0,0,1,.42,8.2A16,16,0,0,1,24.27,19.11Z\"/>",
            "switch": "<path d=\"M5.71,14H20.92V12H5.71L9.42,8.27A1,1,0,1,0,8,6.86L1.89,13,8,19.14a1,1,0,1,0,1.42-1.4Z\"/><rect x=\"23\" y=\"12\" width=\"3\" height=\"2\"/><rect x=\"28\" y=\"12\" width=\"2\" height=\"2\"/><path d=\"M27.92,17.86a1,1,0,0,0-1.42,1.41L30.21,23H15v2H30.21L26.5,28.74a1,1,0,1,0,1.42,1.4L34,24Z\"/><rect x=\"10\" y=\"23\" width=\"3\" height=\"2\"/><rect x=\"6\" y=\"23\" width=\"2\" height=\"2\"/>",
            "port": "<path d=\"M6.06,30a1,1,0,0,1-1-1V8h-2a1,1,0,0,1,0-2h4V29A1,1,0,0,1,6.06,30Z\"/><path d=\"M30.06,27h-25V9h25a3,3,0,0,1,3,3V24A3,3,0,0,1,30.06,27Zm-23-2h23a1,1,0,0,0,1-1V12a1,1,0,0,0-1-1h-23Z\"/><rect x=\"22.06\" y=\"20\" width=\"6\" height=\"2\"/><rect x=\"22.06\" y=\"14\" width=\"6\" height=\"2\"/><path d=\"M19.06,22h-8V20h7V14h2v7A1,1,0,0,1,19.06,22Z\"/>",
            "host": "<path d=\"M18,24.3a2.48,2.48,0,1,0,2.48,2.47A2.48,2.48,0,0,0,18,24.3Zm0,3.6a1.13,1.13,0,1,1,1.13-1.12A1.13,1.13,0,0,1,18,27.9Z\"/><rect x=\"13.5\" y=\"20.7\" width=\"9\" height=\"1.44\"/><path d=\"M25.65,3.6H10.35A1.35,1.35,0,0,0,9,4.95V32.4H27V4.95A1.35,1.35,0,0,0,25.65,3.6Zm-.45,27H10.8V5.4H25.2Z\"/><rect x=\"12.6\" y=\"7.2\" width=\"10.8\" height=\"1.44\"/><rect x=\"12.6\" y=\"10.8\" width=\"10.8\" height=\"1.44\"/>",
            "vm": "<path d=\"M11,5H25V8h2V5a2,2,0,0,0-2-2H11A2,2,0,0,0,9,5v6.85h2Z\"/><path d=\"M30,10H17v2h8v6h2V12h3V26H22V17a2,2,0,0,0-2-2H6a2,2,0,0,0-2,2V31a2,2,0,0,0,2,2H20a2,2,0,0,0,2-2V28h8a2,2,0,0,0,2-2V12A2,2,0,0,0,30,10ZM6,31V17H20v9H16V20H14v6a2,2,0,0,0,2,2h4v3Z\"/>"
        };
        const iconSvg = visuNode.append('g')
            .attr('viewBox', `0 0 ${flowComponent.nodeRadius} ${flowComponent.nodeRadius}`)
            .append('g')
            .attr('transform', `translate(-11, -10) scale(0.60)`)
            .html((d) => iconsSvgCode[d.type]);

        // Labels are written horizontally, centered under their VM: the radius of the circle leaves them room
        const labels = visuNode.append('text')
            .text((d) => d.name)
            .attr('text-anchor', 'middle')
            .attr('y', labelTop + labelFontSize)
            .attr('style', `font-size: ${labelFontSize}px; text-shadow: -1px -1px 0 #fff, 1px -1px 0 #fff, -1px 1px 0 #fff, 1px 1px 0 #fff;`);

        for (let d3elements of [iconSvg, visuNode]) {
            d3elements
                .on("mouseover", function(d3Node, data) {
                    d3.select(this).style("cursor", "pointer");
                    highlight(bundledLinks.filter((link) => link.source.uuid === data.uuid || link.target.uuid === data.uuid), data.uuid);
                })
                .on("mouseout", function(d3Node, data) {
                    d3.select(this).style("cursor", "default");
                    resetHighlight();
                });
        }

        // step1: zoom out, and let d3 recompute positions and sizes
        const [x, y, k] = [0, 0, zoomRatio];
        g.attr('transform', 'translate(' + x + ',' + y + ') scale(' + k + ')');
        flowComponent.svg.call(
            d3.zoom().transform,
            d3.zoomIdentity.translate(x, y).scale(k)
        );

        // step2: center the main group by translating in the middle of the size difference
        // between the group and the svg
        let [x2, y2, k2] = [
            svgWidth / 2,
            svgHeight / 2,
            zoomRatio
        ];

        if (flowComponent.graphParameters.userMovedMap) {
            [x2, y2, k2] = [flowComponent.graphParameters.lastTransformation.x, flowComponent.graphParameters
                .lastTransformation.y, flowComponent.graphParameters.lastTransformation.k
            ];
        }

        g.attr('transform', 'translate(' + x2 + ',' + y2 + ') scale(' + k2 + ')');
        flowComponent.svg.call(
            d3.zoom().transform,
            d3.zoomIdentity.translate(x2, y2).scale(k2)
        );
        // Save initial visualisation's position parameters to enable "zoom-in", "zoom-out" and "reset" buttons to work.
        if (flowComponent.graphParameters.firstTransformation === undefined) {
            flowComponent.graphParameters.firstTransformation = {
                x: x2,
                y: y2,
                k: k2
            }
        }

        // Update legend
        flowComponent.updateLegend(undefined);
    }

    override isOutdated(): boolean {
      return this.flowComponent.selectedView !== "dependencies-viewer";
    }
}

